import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components/macro';
import { ArrowDropDownIcon, Text } from '@idexio/dev-idex-swap-uikit';

const DropDownHeader = styled.div`
  width: 100%;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
  box-shadow: ${({ theme }) => theme.shadows.inset};
  border: 1px solid ${({ theme }) => theme.colors.inputSecondary};
  background: ${({ theme }) => theme.colors.input};
  transition: border-radius 0.15s;
`;

const DropDownListContainer = styled.div`
  width: 168px;
  min-width: 136px;
  height: 0;
  position: absolute;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.input};
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  transition: transform 0.15s, opacity 0.15s;
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;
`;

const DropDownContainer = styled.div<{
  isOpen: boolean;
  width: number;
  height: number;
}>`
  cursor: pointer;
  width: ${({ width }) => width}px;
  position: relative;
  background: ${({ theme }) => theme.colors.input};
  height: 45px;
  width: 168px;
  min-width: 136px;

  ${props =>
    props.isOpen &&
    css`
      ${DropDownHeader} {
        border-bottom: 1px solid ${({ theme }) => theme.colors.inputSecondary};
        box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
      }

      ${DropDownListContainer} {
        height: auto;
        transform: scaleY(1);
        opacity: 1;
        border: 1px solid ${({ theme }) => theme.colors.inputSecondary};
        border-top-width: 0;
        box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
      }
    `}

  svg {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
`;

const ListItem = styled.li<{ $selected: boolean }>`
  list-style: none;
  padding: 8px 16px;
  &:hover {
    background: ${({ theme }) => theme.colors.inputSecondary};
  }
  ${({ $selected }) =>
    $selected &&
    css`
      background: ${({ theme }) => theme.colors.inputSecondary};
      opacity: 0.7;
    `}
`;

interface OptionProps<VALUES extends string> {
  label: string;
  value: VALUES;
}
interface SelectProps<VALUES extends string> {
  options: OptionProps<VALUES>[];
  onChange?: (option: OptionProps<VALUES>) => void;
  selected?: VALUES;
  placeholder?: string;
  untouched?: boolean;
}

function Select<VALUES extends string>({
  options,
  onChange,
  selected,
  placeholder,
  untouched,
}: SelectProps<VALUES>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(option => option.value === selected);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const toggling = () => setIsOpen(!isOpen);

  const onOptionClicked = (option: OptionProps<VALUES>) => () => {
    setIsOpen(false);

    if (onChange) {
      onChange(option);
    }
  };

  useEffect(() => {
    if (!dropdownRef.current) {
      return;
    }
    setContainerSize({
      width: dropdownRef.current.offsetWidth, // Consider border
      height: dropdownRef.current.offsetHeight,
    });
  }, []);

  return (
    <DropDownContainer isOpen={isOpen} ref={containerRef} {...containerSize}>
      {containerSize.width !== 0 && (
        <DropDownHeader onClick={toggling}>
          <Text>
            {untouched
              ? placeholder || 'Select'
              : selectedOption
              ? selectedOption.label
              : placeholder || 'Select'}
          </Text>
        </DropDownHeader>
      )}
      <ArrowDropDownIcon color="text" onClick={toggling} />
      <DropDownListContainer>
        <DropDownList ref={dropdownRef}>
          {options.map(option => (
            <ListItem
              onClick={onOptionClicked(option)}
              key={option.label}
              $selected={option.value === selected}
            >
              <Text>{option.label}</Text>
            </ListItem>
          ))}
        </DropDownList>
      </DropDownListContainer>
    </DropDownContainer>
  );
}

export default Select;
